import { failure, initial, pending, type RemoteData, success } from "@devexperts/remote-data-ts";
import type { Decoder } from "io-ts";
import { useCallback, useState } from "react";
import { type FileRejection, useDropzone as useReactDropzone } from "react-dropzone";
import { FileReaderReject, type ReadFileException, readFile } from "../lib";
import { E } from "../prelude";
export type DropzoneException = ReadFileException | FileReaderReject;

export function useDropzone<I, O>(decoder: Decoder<I, O>) {
  const [dropState, setDropState] = useState<RemoteData<DropzoneException, O>>(initial);
  const onDrop = useCallback(
    (acceptedFiles: Array<File>, fileRejections: Array<FileRejection>) => {
      if (acceptedFiles.length === 1) {
        setDropState(pending);
        readFile(decoder)(acceptedFiles[0])().then(
          E.fold(
            (error) => setDropState(failure(error)),
            (value) => setDropState(success(value)),
          ),
        );
      } else if (fileRejections.length > 0) {
        setDropState(failure(new FileReaderReject(fileRejections.map((r) => r.file))));
      } else {
        setDropState(initial);
      }
    },
    [decoder, setDropState],
  );

  const { getRootProps, getInputProps, isDragActive } = useReactDropzone({
    accept: { "application/json": [".json"] },
    multiple: false,
    onDrop,
  });

  return { dropState, getRootProps, getInputProps, isDragActive };
}
